import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useState } from 'react';
import {
	trackPatternMakerStep,
	ANALYTICS_EVENTS,
} from '../../../services/analytics.service';
import { AuthContext } from '../../../providers/AuthProvider';
import { PatternContext } from '../../../providers/PatternProvider';
import {
	PRICE_TYPES,
	FREE_PRICE,
	PRODUCT_ID_SINGLE,
	PRODUCT_ID_LIBRARY,
} from '../../../config/constants';
import { useStripeCheckout } from '../../../hooks/useStripeCheckout';
import { useStripePrices } from '../../../hooks/useStripePrices';
import { usePurchasePattern } from '../../../hooks/usePurchasePattern';
import { useSnackbar } from '../../../hooks/useSnackbar';
import { OpenPatternButton } from '../Shared/OpenPatternButton';
import { PriceCard } from './PriceCard';
import { SignedInAs } from './SignedInAs';
import { PreviewPatternButton } from './PreviewPatternButton';
import { buttonTypographySx } from '../../../styles/patterns';

const Purchase = (props) => {
	const { escapeCallback, priceType, onGetPatternClick } = props;

	const { user } = useContext(AuthContext);
	const {
		imagePrimary,
		imageSecondary,
		libraryPatternInfo: libraryPatternInfoObj,
		GetPattern,
		height,
		lastOddPage,
		angleLine,
		isInkSaver,
		measurementUnit,
		extraFrontPages,
		extraBackPages,
	} = useContext(PatternContext);

	const {
		subscriptionPrices,
		singlePrices,
		oneTimePrices,
		libraryPrices,
		loading,
	} = useStripePrices();
	const { purchaseSubscription, purchaseOneTime } = useStripeCheckout(
		user,
		GetPattern,
	);
	const { purchasePattern, error: purchaseError } = usePurchasePattern(user);
	const { showSnackbar } = useSnackbar();

	const [subscriptionInProgress, setSubscriptionInProgress] = useState(false);
	const [selectedPriceType, setSelectedPriceType] = useState(
		priceType ?? PRICE_TYPES.ONETIME,
	);

	const { value: libraryPatternInfo } = libraryPatternInfoObj;
	const { metadata: imagePrimaryMetadata } = imagePrimary;
	const { metadata: imageSecondaryMetadata } = imageSecondary;

	useEffect(() => {
		if (imagePrimaryMetadata || imageSecondaryMetadata) {
			setSelectedPriceType(PRICE_TYPES.LIBRARY);
		}
	}, [imagePrimaryMetadata, imageSecondaryMetadata]);

	useEffect(() => {
		if (purchaseError) {
			showSnackbar(purchaseError, 'error');
		}
	}, [purchaseError, showSnackbar]);

	useEffect(() => {
		trackPatternMakerStep(ANALYTICS_EVENTS.PATTERN_MAKER_PRICING_VIEWED);
	}, []);

	if (loading) {
		return <CircularProgress sx={{ margin: '20px' }} />;
	}

	// Get the correct library price based on the libraryPatternInfo of this image
	const getLibraryPrice = () => {
		const { priceId } = libraryPatternInfo;
		const price = libraryPrices.find((price) => price.id === priceId);

		// return a 'free' price object if no price is found
		if (!price) {
			return FREE_PRICE;
		}

		return price;
	};

	const purchaseSinglePattern = async (priceId) => {
		const isLibrary = !!(imagePrimaryMetadata || imageSecondaryMetadata);
		await purchasePattern({
			type: isLibrary ? 'library' : 'user',
			fileName: imagePrimaryMetadata ?? null,
			fileName2: imageSecondaryMetadata ?? null,
			imagePrimaryDataURL: isLibrary ? null : imagePrimary.dataURL,
			imageSecondaryDataURL: isLibrary ? null : imageSecondary.dataURL,
			bookParams: {
				height: height.value,
				lastOddPage: lastOddPage.value,
				angleLine: angleLine.value,
				isInkSaver: isInkSaver.value,
				measurementUnit: measurementUnit.value,
				extraFrontPages: extraFrontPages.value,
				extraBackPages: extraBackPages.value,
			},
			priceId,
			productId: isLibrary ? PRODUCT_ID_LIBRARY : PRODUCT_ID_SINGLE,
		});
	};

	const checkoutProps = {
		inProgress: [subscriptionInProgress, setSubscriptionInProgress],
		purchaseSubscription,
		purchaseSinglePattern,
		purchaseOneTime,
	};

	const getLibraryPatternPriceCardOrOpenButton = (
		loading,
		onGetPatternClick,
	) => {
		const { priceId } = libraryPatternInfo;

		// Free pattern: no priceId, or the priceId resolves to FREE_PRICE
		const libraryPrice = getLibraryPrice();
		if (
			!priceId ||
			priceId === FREE_PRICE.id ||
			libraryPrice.id === FREE_PRICE.id
		) {
			return (
				<OpenPatternButton
					loading={loading}
					onGetPatternClick={onGetPatternClick}
				/>
			);
		}

		//Paid Pattern, show price card
		return (
			<>
				<PriceCard
					priceInfo={libraryPrice}
					escapeCallback={escapeCallback}
					{...checkoutProps}
				/>
				<PreviewPatternButton
					loading={loading}
					onGetPatternClick={onGetPatternClick}
				/>
			</>
		);
	};

	return (
		<>
			<SignedInAs />
			{selectedPriceType === PRICE_TYPES.LIBRARY && libraryPrices && (
				<>
					{getLibraryPatternPriceCardOrOpenButton(
						loading,
						onGetPatternClick,
					)}
				</>
			)}

			{selectedPriceType !== PRICE_TYPES.LIBRARY && (
				<div style={{ marginTop: '20px', textAlign: 'center' }}>
					<Typography variant="h4" sx={{ mb: '-10px' }}>
						Pick the plan that suits you best
					</Typography>
					<Typography variant="h6">
						Select a one-time purchase or subscribe to save
					</Typography>
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							marginTop: '20px',
							marginBottom: '20px',
						}}
					>
						<Button
							variant={
								selectedPriceType === PRICE_TYPES.ONETIME
									? 'contained'
									: 'text'
							}
							color="primary"
							onClick={() =>
								setSelectedPriceType(PRICE_TYPES.ONETIME)
							}
							sx={{ minWidth: '100px' }}
						>
							<Typography variant="h6" sx={buttonTypographySx}>
								One Time
							</Typography>
						</Button>
						<Button
							variant={
								selectedPriceType === PRICE_TYPES.SUBSCRIPTION
									? 'contained'
									: 'text'
							}
							color="primary"
							onClick={() =>
								setSelectedPriceType(PRICE_TYPES.SUBSCRIPTION)
							}
							sx={{ minWidth: '100px' }}
						>
							<Typography variant="h6" sx={buttonTypographySx}>
								Subscription
							</Typography>
						</Button>
					</div>
					{selectedPriceType === PRICE_TYPES.ONETIME && (
						<Grid
							container
							gap={3}
							sx={{
								alignItems: 'center',
								justifyContent: 'center',
								width: '100%',
							}}
						>
							{singlePrices && (
								<>
									<PriceCard
										priceInfo={singlePrices[0]}
										escapeCallback={escapeCallback}
										{...checkoutProps}
									/>
								</>
							)}
							{oneTimePrices && (
								<>
									<PriceCard
										priceInfo={oneTimePrices[0]}
										{...checkoutProps}
									/>
								</>
							)}
						</Grid>
					)}
					{selectedPriceType === PRICE_TYPES.SUBSCRIPTION && (
						<Grid
							container
							gap={3}
							sx={{
								alignItems: 'center',
								justifyContent: 'center',
								width: '100%',
							}}
						>
							{subscriptionPrices && (
								<>
									<PriceCard
										priceInfo={subscriptionPrices.find(
											(a) => {
												return (
													a.data.interval === 'month'
												);
											},
										)}
										{...checkoutProps}
									/>
									<PriceCard
										priceInfo={subscriptionPrices.find(
											(a) => {
												return (
													a.data.interval === 'year'
												);
											},
										)}
										{...checkoutProps}
									/>
								</>
							)}
						</Grid>
					)}
					<PreviewPatternButton
						loading={loading}
						onGetPatternClick={onGetPatternClick}
					/>
				</div>
			)}
		</>
	);
};

export { Purchase };
